import { FC, useMemo } from "react";
import { Formik, FormikHelpers } from "formik";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import {
    TestTypePrerequisiteFieldsFragment,
    useTestTypePrerequisiteQuery,
    useUpdateTestTypePrerequisiteMutation,
} from "app/api/graph/types";
import yup from "app/util/yup.ts";
import { StatusAlert } from "app/component/form/StatusAlert";
import { handleError } from "app/api/graph/form.ts";
import { testTypePrerequisiteGraphId } from "app/api/graph/helpers";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";

type Props = {
    testTypePrerequisiteId: number;
    onUpdate?: (testTypePrerequisite: TestTypePrerequisiteFieldsFragment) => void;
};

type FormValues = {
    description: string;
    replacePrevious?: boolean;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    description: yup.string().required(),
    replacePrevious: yup.boolean(),
});

export const EditTestTypePrerequisiteForm: FC<Props> = ({ testTypePrerequisiteId, onUpdate }) => {
    const { data, loading, error } = useTestTypePrerequisiteQuery({
        variables: { testTypePrerequisiteId: testTypePrerequisiteGraphId(testTypePrerequisiteId) },
    });
    const testTypePrerequisite = data?.testTypePrerequisite;
    const [updateTestTypePrerequisite] = useUpdateTestTypePrerequisiteMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            description: testTypePrerequisite?.description ?? "",
            replacePrevious: false,
        }),
        [testTypePrerequisite],
    );

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateTestTypePrerequisite({
            variables: {
                input: {
                    id: testTypePrerequisiteGraphId(testTypePrerequisiteId),
                    description: values.description,
                },
            },
        })
            .then((response) =>
                response.data?.updateTestTypePrerequisite?.testTypePrerequisite
                    ? onUpdate?.(response.data?.updateTestTypePrerequisite?.testTypePrerequisite)
                    : undefined,
            )
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!testTypePrerequisite) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, values, isSubmitting, errors, touched, status, handleChange, handleBlur }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            name="description"
                            value={values.description ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.description && !!errors.description}
                            autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            id="replace-previous"
                            type="checkbox"
                            name="replacePrevious"
                            label="Replace previous version?"
                            checked={values.replacePrevious ?? false}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.replacePrevious && !!errors.replacePrevious}
                            feedback={errors.replacePrevious}
                        />
                        <Form.Text muted>
                            Whether or not to update report prerequisites with this new version.
                        </Form.Text>
                        {values.replacePrevious ? (
                            <Alert variant="warning">
                                This will update report prerequisites that are based on the latest version of this
                                prerequisite, while those based on older versions will be untouched. Unmodified fields
                                on report prerequisites will be updated to match this new version, while modified fields
                                (those that are identical to this master prerequisite) will be untouched.
                            </Alert>
                        ) : (
                            <Alert variant="warning">
                                A new version of this prerequisite will be created. Report prerequisites attached to the
                                current (or previous) version of this prerequisite will not be updated. This new version
                                will only be used by new report prerequisites created after this update.
                            </Alert>
                        )}
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
